import { z } from "zod";
import { MailMode } from "./mailDelivery.js";

/**
 * 請求書のまとめて発行・まとめて PDF・まとめてメール（#267 3c、docs/97 決めごと13・§8 の5。門はモジュール sales・両製品）。
 *   締めの月末の作業（締めの画面で下書きをまとめて作成 → 請求書の一覧で選んで まとめて発行 → まとめて PDF／メール）。E1.5（定期請求）もこれを使う。
 *   API は今の1枚の口（POST /invoices/:id/issue・GET /invoices/:id/pdf・POST /mail-deliveries）を順に呼ぶ＝1枚ずつ出したのと同じ番号・紙・送付の記録になる
 */

/** 1回に選べる枚数 */
export const INVOICE_BULK_LIMITS = { issue: 50, pdf: 50, mail: 30 } as const;

const Ids = (max: number) =>
  z
    .array(z.string().uuid())
    .min(1)
    .max(max, `一度に選べるのは ${max}枚までです`)
    .refine((xs) => new Set(xs).size === xs.length, "同じ請求書が2回選ばれています");

/** POST /invoices/bulk-issue：選んだ請求書のうち下書きを、請求日の古い順（同じ日は送った ids の順＝画面の窓の並び）に1枚ずつ発行する。出せない1枚で止め、出た分は出たまま */
export const InvoiceBulkIssue = z.object({ ids: Ids(INVOICE_BULK_LIMITS.issue) });
export type InvoiceBulkIssue = z.infer<typeof InvoiceBulkIssue>;
export const InvoiceBulkIssueResult = z.object({
  /** 出した請求書（出した順＝番号の順） */
  issued: z.array(z.object({ id: z.string().uuid(), documentNo: z.string() })),
  /** 出せなかった1枚（ここで止めた）。無ければ null */
  failed: z.object({ id: z.string().uuid(), clientName: z.string(), code: z.string(), message: z.string() }).nullable(),
  /** 止めたので出していない下書き */
  notDone: z.array(z.string().uuid()),
  /** 下書きでないので飛ばした請求書（発行済・取り消し＝voided） */
  skipped: z.array(z.string().uuid()),
});
export type InvoiceBulkIssueResult = z.infer<typeof InvoiceBulkIssueResult>;

/** POST /invoices/bulk-pdf：選んだ請求書の PDF（1枚ずつの紙と同じバイト）を、1つの PDF にまとめるか ZIP に。並びは請求日の古い順 */
export const InvoiceBulkPdfFormat = z.enum(["merged", "zip"]);
export type InvoiceBulkPdfFormat = z.infer<typeof InvoiceBulkPdfFormat>;
export const InvoiceBulkPdf = z.object({ ids: Ids(INVOICE_BULK_LIMITS.pdf), format: InvoiceBulkPdfFormat.default("merged") });
export type InvoiceBulkPdf = z.infer<typeof InvoiceBulkPdf>;

/** POST /invoices/bulk-mail/preview：送る前の確かめ（請求書ごとの宛先と渡し方。メールの初めの値＝1枚のメールの枠と同じ） */
export const InvoiceBulkMailRequest = z.object({ ids: Ids(INVOICE_BULK_LIMITS.mail) });
export type InvoiceBulkMailRequest = z.infer<typeof InvoiceBulkMailRequest>;
export const InvoiceBulkMailRow = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  clientName: z.string(),
  total: z.number().int(),
  to: z.array(z.string()),
  mode: MailMode,
  /** 送れるか（発行済で宛先がある） */
  ok: z.boolean(),
  /** 送れないわけ（宛先なし・下書き など） */
  reason: z.string().nullable(),
});
export type InvoiceBulkMailRow = z.infer<typeof InvoiceBulkMailRow>;
export const InvoiceBulkMailPreview = z.object({
  rows: z.array(InvoiceBulkMailRow),
  /** 今日すでに送った通数と1日の上限（帳票と見積依頼書を合わせて） */
  sentToday: z.number().int(),
  perDay: z.number().int(),
});
export type InvoiceBulkMailPreview = z.infer<typeof InvoiceBulkMailPreview>;
/** POST /invoices/bulk-mail：送れる請求書を1通ずつ（間を空けて）送る。1枚の失敗では止めず、送信の不調・1日の上限で止める */
export const InvoiceBulkMailResult = z.object({
  sent: z.array(z.object({ id: z.string().uuid(), deliveryId: z.string().uuid() })),
  failed: z.array(z.object({ id: z.string().uuid(), clientName: z.string(), code: z.string(), message: z.string() })),
  /** 止めたので送っていない請求書 */
  notDone: z.array(z.string().uuid()),
  /** 止めたわけ：rate＝全社の送る速さ（数分後に送れる）／day＝1日の上限／send＝送信の不調。止めていなければ null */
  stoppedBy: z.enum(["rate", "day", "send"]).nullable(),
  /** 送信待ちに積んだ請求書（#268 1.5c-2：rate・day で止めた残り＝枠が空き次第 API の見張りが送る） */
  queued: z.array(z.string().uuid()).default([]),
});
export type InvoiceBulkMailResult = z.infer<typeof InvoiceBulkMailResult>;

/** 文（ボタンに「を」「の」を入れない・見出しは名詞だけ＝docs/10 §5） */
export const INVOICE_BULK_MSG = {
  selected: (n: number) => `${n}枚 選択`,
  breakdown: (drafts: number, issued: number) => [drafts ? `下書き ${drafts}` : "", issued ? `発行済 ${issued}` : ""].filter(Boolean).join("・"),
  deselect: "選択解除",
  issue: "まとめて発行",
  pdf: "まとめて PDF",
  mail: "まとめてメール",
  pickAll: "すべて選択",
  overLimit: (n: number) => `一度に選べるのは ${n}枚までです`,
  mailOverLimit: (n: number) => `まとめてメールは ${n}通まで`,
  pick: (name: string) => `${name} を選ぶ`,
  /** まとめて発行の窓 */
  issueLead: "番号は上から順に付きます。発行したあとは書き換えられません。",
  issueHelp: "選んだ請求書のうち下書きだけを、請求日の古い順に1枚ずつ発行します。出せない請求書があればそこで止め、それまでに出した請求書は発行済のままです。分割払いは各請求書の設定（{customer}の設定か、その請求書で選んだもの）のとおりです。",
  issueBtn: "発行",
  colResult: "結果",
  resultStopped: "ここで停止",
  resultNotDone: "未発行",
  issued: (n: number) => `${n}枚 発行しました`,
  issueStopped: (name: string, message: string) => `${name}の請求書で止めました：${message}`,
  notDone: (n: number) => `${n}枚は発行していません`,
  /** まとめて PDF の窓 */
  pdfLead: "選んだ請求書の紙を、請求日の古い順に並べて保存します。",
  formatMerged: "1つの PDF",
  formatZip: "ZIP（1枚ずつ）",
  pdfDraftNote: (n: number) => `下書き ${n}枚は透かし入りです`,
  pdfBtn: "保存",
  pdfMaking: "PDF を作成中…",
  /** まとめてメールの窓 */
  mailLead: "発行済の請求書を、{customer}ごとのメールの宛先と文面で1通ずつ送ります。",
  mailHelp: "宛先・件名・本文は、1枚ずつ送るときの初めの値（{customer}担当者のメール・設定のメールの文面）です。宛先の無い請求書は送りません。宛先や文面を変えたいときは、その請求書の紙の画面から1枚ずつ送ってください。",
  mailNoTo: "宛先なし（送りません）",
  mailDraft: "下書き（発行してから）",
  mailModePage: "閲覧ページ",
  mailModeAttach: "添付",
  mailCount: (n: number, today: number, perDay: number) => `${n}通 送信（本日 ${today}／${perDay}通）`,
  mailOverDay: (left: number, rest: number) => `本日はあと ${left}通まで送れます。残り ${rest}通は送信待ちにして明日送ります`,
  mailBtn: "送信",
  mailResultSent: "送信済",
  mailResultNotDone: "未送信",
  mailResultFailed: "送信失敗",
  mailResultQueued: "送信待ち",
  mailRateRest: (n: number) => `残り ${n}通は数分後に送れます`,
  mailDayRest: (n: number) => `本日の上限に達しました。残り ${n}通は明日送れます`,
  /** #268 1.5c-2：残りを送信待ちに積んだ */
  mailRateQueued: (n: number) => `残り ${n}通は送信待ちにしました。数分ごとに送ります`,
  mailDayQueued: (n: number) => `本日の上限に達しました。残り ${n}通は送信待ちにしました。明日送ります`,
  mailLoading: "宛先を確認中…",
  mailSent: (n: number) => `${n}通 送信しました`,
  mailFailed: (name: string, message: string) => `${name}：${message}`,
  colNo: "番号",
  colClient: "{customer}",
  colSubject: "件名",
  colDate: "請求日",
  colTotal: "金額（税込）",
  colTo: "宛先",
  colMode: "渡し方",
} as const;
